import type { ActivityEntry, OperatorProfile, ProfilesSyncResponse, RealProfile } from '../types/profiles';

const CACHE_KEY = 'conectar-cache:real-profiles-v2';

interface CachedSync {
  profiles: RealProfile[];
  operators: OperatorProfile[];
  activity: ActivityEntry[];
}

const EMPTY_SYNC: CachedSync = { profiles: [], operators: [], activity: [] };

export function loadCachedRealProfiles(): CachedSync {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw) as CachedSync;
  } catch {
    /* cache corrompido — ignora, próxima sync reescreve */
  }
  return EMPTY_SYNC;
}

function writeCachedRealProfiles(data: CachedSync) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('Cache local de perfis cheio, seguindo sem persistir:', err);
  }
}

const SYNC_INTERVAL_MS = 5 * 60_000; // 5 minutos entre chamadas ao servidor
const LAST_SYNC_TS_KEY = 'conectar-last-profile-sync-ts-v2';

export async function syncRealProfilesFromServer(idToken: string): Promise<CachedSync> {
  const lastSync = Number(localStorage.getItem(LAST_SYNC_TS_KEY) ?? 0);
  if (Date.now() - lastSync < SYNC_INTERVAL_MS) {
    // Dentro do intervalo → retorna do cache local sem bater no servidor.
    return loadCachedRealProfiles();
  }

  const res = await fetch('/api/profiles/sync', {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { detail?: string };
      detail = body.detail ?? '';
    } catch {
      /* ignora erro ao parsear corpo */
    }
    const msg = `[/api/profiles/sync] HTTP ${res.status}${detail ? ` → ${detail}` : ''}`;
    console.error(msg);
    throw new Error(msg);
  }

  const json = (await res.json()) as ProfilesSyncResponse;
  const data: CachedSync = { profiles: json.profiles, operators: json.operators, activity: json.activity };
  localStorage.setItem(LAST_SYNC_TS_KEY, String(Date.now()));
  writeCachedRealProfiles(data);
  return data;
}
